import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Components
import Sidebar from '../main/Sidebar';
import Topbar from '../main/Topbar';

// Utils
import { CommonUtils } from '../../utils/commonUtils';
import { getUserList, deleteUser, User, UserListRequest } from '../../utils/user';
import { UserFormUtils } from '../../utils/userCommon';
import { 
  Menu, 
  Bell, 
  User as UserIcon, 
  Users, 
  Settings,
  LogOut,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Search,
  Filter,
  Plus,
  Edit,
  Trash2,
  Eye,
  ArrowUpDown,
  Home,
  Download
} from 'lucide-react';

const UserList: React.FC = () => {
  // Router hooks
  const navigate = useNavigate();

  // UI state
  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('users');

  // List state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sortField, setSortField] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  // API state
  const [users, setUsers] = useState<User[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // User info state
  const [userInfo, setUserInfo] = useState<any>(null);

  // Check if screen is mobile size
  UserFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  // 사용자 정보 로드
  useEffect(() => {
    const info = UserFormUtils.loadUserInfo();
    if (info) {
      setUserInfo(info);
    }
  }, []);

  // 사용자 목록 조회
  useEffect(() => {
    const fetchUsers = async () => {
      if (!userInfo) {
        console.log('사용자 정보가 없습니다.');
        return;
      }
      
      try {
        setIsLoading(true);
        setError(null);
        
        const accountCode = userInfo.accountCode;
        const request: UserListRequest = {
          page: currentPage,
          size: pageSize,
          sort: `${sortField},${sortOrder}`,
          status: selectedStatus !== 'all' ? selectedStatus : undefined,
          search: searchTerm || undefined
        };
        
        console.log('사용자 목록 API 호출:', { accountCode, request });
        const response = await getUserList(accountCode, request);
        console.log('사용자 목록 API 응답:', response);
        
        if (response && response.users) {
          setUsers(response.users);
          setTotalCount(response.totalCount);
        } else {
          setUsers([]);
          setTotalCount(0);
        }
      } catch (err) {
        console.error('사용자 목록 조회 오류:', err);
        const displayMessage = CommonUtils.getNetworkErrorMessage(err);
        setError(displayMessage);
      } finally {
        setIsLoading(false);
      }
    };

    if (userInfo) {
      fetchUsers();
    }
  }, [userInfo, currentPage, pageSize, sortField, sortOrder, selectedStatus, searchTerm]);

  // Helper functions
  const getStatusBadge = (status: string) => {
    const badgeInfo = UserFormUtils.getStatusBadge(status);
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badgeInfo.color}`}>
        <span className={`w-1.5 h-1.5 mr-1 rounded-full ${badgeInfo.color.includes('green') ? 'bg-green-400' : 'bg-gray-400'}`}></span>
        {badgeInfo.text}
      </span>
    );
  };

  const formatDate = (dateString: string): string => {
    return UserFormUtils.formatDate(dateString);
  };

  const handleSort = (field: string) => {
    UserFormUtils.handleSort(field, sortField, sortOrder, setSortField, setSortOrder);
  };

  const handleSelectAll = () => {
    UserFormUtils.handleSelectAll(selectedUsers, users, setSelectedUsers);
  };

  const handleSelectUser = (userId: string) => {
    UserFormUtils.handleSelectUser(userId, selectedUsers, setSelectedUsers);
  };

  // Page change handler
  const handlePageChange = (page: number) => {
    UserFormUtils.handlePageChange(page, setCurrentPage);
  };

  // Page size change handler
  const handlePageSizeChange = (size: number) => {
    UserFormUtils.handlePageSizeChange(size, setPageSize, setCurrentPage);
  };

  // Delete user handler
  const handleDeleteUser = async (userId: string, userName: string) => {
    if (!confirm(`${userName}을(를) 삭제하시겠습니까?`)) {
      return;
    }

    if (!userInfo) {
      alert('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
      return;
    }

    try {
      setIsLoading(true);
      const accountCode = userInfo.accountCode;
      await deleteUser(accountCode, userId);
      alert('사용자가 삭제되었습니다.');
      // 목록 새로고침
      const request: UserListRequest = {
        page: currentPage,
        size: pageSize,
        sort: `${sortField},${sortOrder}`,
        status: selectedStatus !== 'all' ? selectedStatus : undefined,
        search: searchTerm || undefined
      };
      const response = await getUserList(accountCode, request);
      if (response && response.users) {
        setUsers(response.users);
        setTotalCount(response.totalCount);
      }
    } catch (error: any) {
      console.error('사용자 삭제 오류:', error);
      // 서버에서 보낸 에러 메시지 우선 사용
      const displayMessage = error?.message || CommonUtils.getNetworkErrorMessage(error) || '사용자 삭제 중 오류가 발생했습니다.';
      alert(displayMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Delete selected users handler
  const handleDeleteSelected = async () => {
    if (selectedUsers.length === 0) {
      alert('삭제할 사용자를 선택해주세요.');
      return;
    }

    if (!confirm(`선택한 ${selectedUsers.length}명의 사용자를 삭제하시겠습니까?`)) {
      return;
    }

    if (!userInfo) {
      alert('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
      return;
    }

    try {
      setIsLoading(true);
      const accountCode = userInfo.accountCode;
      let successCount = 0;
      let failCount = 0;
      const errors: string[] = [];

      // 각 사용자를 순차적으로 삭제
      for (const userId of selectedUsers) {
        try {
          await deleteUser(accountCode, userId);
          successCount++;
        } catch (error: any) {
          failCount++;
          const user = users.find(u => u.userId === userId);
          const userName = user ? user.name : userId;
          // 서버에서 보낸 에러 메시지 우선 사용
          const errorMsg = error?.message || '삭제 실패';
          errors.push(`${userName}: ${errorMsg}`);
        }
      }

      // 결과 메시지 표시
      if (failCount === 0) {
        alert(`모든 사용자(${successCount}명)가 삭제되었습니다.`);
      } else {
        alert(`삭제 완료: ${successCount}명, 실패: ${failCount}명\n\n실패한 사용자:\n${errors.join('\n')}`);
      }

      // 선택 초기화
      setSelectedUsers([]);

      // 목록 새로고침
      const request: UserListRequest = {
        page: currentPage,
        size: pageSize,
        sort: `${sortField},${sortOrder}`,
        status: selectedStatus !== 'all' ? selectedStatus : undefined,
        search: searchTerm || undefined
      };
      const response = await getUserList(accountCode, request);
      if (response && response.users) {
        setUsers(response.users);
        setTotalCount(response.totalCount);
      }
    } catch (error: any) {
      console.error('사용자 삭제 오류:', error);
      // 서버에서 보낸 에러 메시지 우선 사용
      const displayMessage = error?.message || CommonUtils.getNetworkErrorMessage(error) || '사용자 삭제 중 오류가 발생했습니다.';
      alert(displayMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      <Sidebar
        isDesktopSidebarOpen={isDesktopSidebarOpen}
        setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        isProfileDropdownOpen={isProfileDropdownOpen}
        setIsProfileDropdownOpen={setIsProfileDropdownOpen}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Topbar
          title="사용자 관리"
          subtitle="콘솔에 로그인할 수 있는 사용자를 관리하세요"
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
        />

        {/* User List Content */}
        <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <div className="max-w-full">
            {/* Filters and Actions */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
              <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
                <div className="flex flex-col sm:flex-row gap-3 flex-1 w-full">
                  {/* Search */}
                  <div className="relative flex-1 sm:max-w-xs min-w-[120px]">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="사용자 아이디, 이름, 부서로 검색"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Status Filter */}
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">전체 상태</option>
                    <option value="ACTIVE">활성</option>
                    <option value="INACTIVE">비활성</option>
                  </select>
                </div>

                <div className="flex gap-2 w-full sm:w-auto">
                  {/* Excel Download */}
                  <button
                    onClick={() => alert('엑셀 다운로드 기능은 준비중입니다.')}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    엑셀
                  </button>

                  {/* Add User Button */}
                  <button 
                    onClick={() => navigate('/users/create')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    사용자 추가
                  </button>
                </div>
              </div>

              {/* Summary */}
              <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  총 <span className="font-medium text-gray-900">{totalCount}</span>명
                </p>
                <div className="flex items-center gap-4 text-sm">
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-1"></span>
                    활성: {users.filter(u => u.status?.toLowerCase() === 'ACTIVE').length}
                  </span>
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-gray-400 rounded-full mr-1"></span>
                    비활성: {users.filter(u => u.status?.toLowerCase() === 'INACTIVE').length}
                  </span>
                </div>
              </div>
            </div>

            {/* Error State */}
            {error && (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="text-red-500 mb-4">
                  <UserIcon className="w-12 h-12 mx-auto" />
                </div>
                <p className="text-red-600 mb-4 text-center max-w-md mx-auto">{error}</p>
                <button
                  onClick={() => {
                    setError(null);
                    window.location.reload();
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  다시 시도
                </button>
              </div>
            )}

            {/* Table View - 에러가 없을 때만 표시 */}
            {!error && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="pl-4 pr-0 py-3 text-left">
                          <input
                            type="checkbox"
                            checked={selectedUsers.length === users.length && users.length > 0}
                            onChange={handleSelectAll}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </th>
                        <th className="pl-6 pr-0 py-3 text-left">
                          <button
                            onClick={() => handleSort('userId')}
                            className="group inline-flex items-center text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                          >
                            사용자 아이디
                            <ArrowUpDown className="ml-1 h-3 w-3" />
                          </button>
                        </th>
                        <th className="pl-6 pr-0 py-3 text-left">
                          <button
                            onClick={() => handleSort('name')}
                            className="group inline-flex items-center text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                          >
                            사용자명
                            <ArrowUpDown className="ml-1 h-3 w-3" />
                          </button>
                        </th>
                        <th className="pl-6 pr-0 py-3 text-left">
                          <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                            상태
                          </span>
                        </th>
                        <th className="pl-6 pr-0 py-3 text-left">
                          <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                            부서
                          </span>
                        </th>
                        <th className="pl-6 pr-0 py-3 text-left">
                          <button
                            onClick={() => handleSort('createdAt')}
                            className="group inline-flex items-center text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                          >
                            생성시각
                            <ArrowUpDown className="ml-1 h-3 w-3" />
                          </button>
                        </th>
                        <th className="relative px-6 py-3">
                          <span className="sr-only">작업</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {isLoading ? (
                        <tr>
                          <td colSpan={7} className="px-6 py-12 text-center">
                            <div className="flex justify-center">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            </div>
                          </td>
                        </tr>
                      ) : error ? (
                        <tr>
                          <td colSpan={7} className="px-6 py-12 text-center">
                            <div className="flex flex-col items-center">
                              <div className="text-red-500 mb-2">
                                <UserIcon className="w-8 h-8 mx-auto" />
                              </div>
                              <p className="text-sm text-red-600 mb-2">{error}</p>
                              <button
                                onClick={() => window.location.reload()}
                                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                              >
                                다시 시도
                              </button>
                            </div>
                          </td>
                        </tr>
                      ) : users.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-6 py-12 text-center">
                            <div className="flex flex-col items-center">
                              <UserIcon className="w-8 h-8 text-gray-400 mb-2" />
                              <p className="text-sm text-gray-500">등록된 사용자가 없습니다.</p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        users.map((user) => (
                          <tr key={user.userId} className="hover:bg-gray-50">
                            <td className="pl-4 pr-0 py-4 whitespace-nowrap">
                              <input
                                type="checkbox"
                                checked={selectedUsers.includes(user.userId)}
                                onChange={() => handleSelectUser(user.userId)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                            </td>
                            <td className="pl-6 pr-0 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {user.userId}
                            </td>
                            <td className="pl-6 pr-0 py-4 whitespace-nowrap text-sm text-gray-900">
                              {user.name}
                            </td>
                            <td className="pl-6 pr-0 py-4 whitespace-nowrap">
                              {getStatusBadge(user.status)}
                            </td>
                            <td className="pl-6 pr-0 py-4 whitespace-nowrap text-sm text-gray-500">
                              {user.department || '-'}
                            </td>
                            <td className="pl-6 pr-0 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(user.createdAt)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex items-center justify-end space-x-2">
                                <button
                                  className="text-gray-400 hover:text-gray-600"
                                  title="상세 보기"
                                  onClick={() => {
                                    alert(`${user.name} 상세보기 기능은 준비중입니다.`);
                                  }}
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                                <button
                                  className="text-gray-400 hover:text-blue-600"
                                  title="수정"
                                  onClick={() => {
                                    UserFormUtils.handleUserEdit(navigate, user.userId, currentPage, pageSize);
                                  }}
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  className="text-gray-400 hover:text-red-600"
                                  title="삭제"
                                  onClick={() => handleDeleteUser(user.userId, user.name)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Selected Actions Bar */}
                {selectedUsers.length > 0 && (
                  <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">
                        {selectedUsers.length}명 선택됨
                      </span>
                      <div className="flex items-center space-x-2">
                        <button className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50">
                          상태 변경
                        </button>
                        <button className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50">
                          엑셀 다운로드
                        </button>
                        <button 
                          onClick={handleDeleteSelected}
                          className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                        >
                          선택 삭제
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Pagination */}
            <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700">페이지당</span>
                  <select 
                    value={pageSize}
                    onChange={(e) => {
                      handlePageSizeChange(parseInt(e.target.value));
                    }}
                    className="px-1 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="10">10개</option>
                    <option value="20">20개</option>
                    <option value="50">50개</option>
                    <option value="100">100개</option>
                  </select>
                  <span>표시</span>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handlePageChange(0)}
                    disabled={currentPage === 0}
                    className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    처음
                  </button>
                  <button
                    onClick={() => handlePageChange(Math.max(0, currentPage - 1))}
                    disabled={currentPage === 0}
                    className="px-2 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  
                  {/* Page Numbers */}
                  {Array.from({ length: Math.ceil(totalCount / pageSize) }, (_, i) => i).slice(0, 5).map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-3 py-1 rounded text-sm ${
                        currentPage === page
                          ? 'bg-blue-600 text-white'
                          : 'border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page + 1}
                    </button>
                  ))}
                  
                  <button
                    onClick={() => handlePageChange(Math.min(Math.ceil(totalCount / pageSize) - 1, currentPage + 1))}
                    disabled={currentPage >= Math.ceil(totalCount / pageSize) - 1}
                    className="px-2 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handlePageChange(Math.ceil(totalCount / pageSize) - 1)}
                    disabled={currentPage >= Math.ceil(totalCount / pageSize) - 1}
                    className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    마지막
                  </button>
                </div>

                <p className="text-sm text-gray-600">
                  {currentPage * pageSize + 1} - {Math.min((currentPage + 1) * pageSize, totalCount)} / 총 {totalCount}명
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default UserList;